import React, { useEffect, useState, useMemo } from 'react';
import Nlayout from './Nlayout';
import {
    Box, Typography, Button, TextField,
    Paper, CircularProgress, Alert, IconButton, Tooltip,
    Chip, Grid, Card
} from '@mui/material';
import axios from 'axios';
import { FaSearch, FaCopy, FaCheck, FaWallet, FaDownload } from 'react-icons/fa';
import { FiRefreshCw, FiCheckCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function ListadoBNB({ isMobile: propsIsMobile }) {
    const [solicitudesData, setSolicitudesData] = useState({ total: 0, nuevas: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('todas'); // 'todas', 'nuevas', 'vistas'
    const [seenAddresses, setSeenAddresses] = useState([]);
    const [copiedIndex, setCopiedIndex] = useState(null);

    // Detectar pantalla móvil internamente si no viene por prop
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const isMobile = propsIsMobile !== undefined ? propsIsMobile : windowWidth <= 768;

    // Cargar direcciones vistas desde localStorage
    useEffect(() => {
        try {
            const saved = localStorage.getItem('seen_bnb_addresses_v1');
            if (saved) {
                setSeenAddresses(JSON.parse(saved));
            }
        } catch (e) {
            console.error('Error cargando direcciones vistas:', e);
        }
    }, []);

    // Obtener las solicitudes desde el backend
    const fetchSolicitudes = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('https://backend.eldenguee.com/solicitudes/historico', {
                headers: {
                    "x-internal-api-key": process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026',
                    "x-api-key": process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026'
                }
            });
            const data = response.data;
            if (data && typeof data === 'object') {
                if (Array.isArray(data)) {
                    setSolicitudesData({ total: data.length, nuevas: data });
                } else {
                    setSolicitudesData({
                        total: data.total || 0,
                        nuevas: Array.isArray(data.nuevas) ? data.nuevas : []
                    });
                }
            }
        } catch (err) {
            try {
                const resSec = await axios.get('https://backend.eldenguee.com/solicitudes/tipeos');
                const dataSec = resSec.data;
                if (Array.isArray(dataSec)) {
                    setSolicitudesData({ total: dataSec.length, nuevas: dataSec });
                } else {
                    setSolicitudesData({
                        total: dataSec?.total || 0,
                        nuevas: Array.isArray(dataSec?.nuevas) ? dataSec.nuevas : []
                    });
                }
            } catch (e2) {
                setError('Error al cargar las solicitudes: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSolicitudes();
    }, []);

    // Procesar lista de direcciones BNB y usuarios asociando su estado (NUEVA / VISTA)
    const itemsBNB = useMemo(() => {
        const rawList = solicitudesData.nuevas;
        if (!Array.isArray(rawList)) return [];

        const list = [];
        rawList.forEach((sol, idx) => {
            const bnb = sol.bnbAddress || sol.bnb_address || sol.bnb;
            const usuario = sol.nombre || sol.usuario || sol.kick_user || 'Usuario Anónimo';

            if (bnb && typeof bnb === 'string' && bnb.trim() !== '') {
                const cleanBnb = bnb.trim();
                // Si el backend viene con isNew explícito, o bien filtramos por localStorage
                const isNewLocally = !seenAddresses.includes(cleanBnb.toLowerCase());
                const isNew = sol.isNew !== undefined ? (Boolean(sol.isNew) && isNewLocally) : isNewLocally;

                list.push({
                    id: sol._id || sol.id || `bnb_${idx}`,
                    usuario: usuario,
                    bnbAddress: cleanBnb,
                    fecha: sol.created_at || sol.fecha || sol.createdAt || sol.date || '',
                    status: sol.status || 'pendiente',
                    isNew: isNew
                });
            }
        });
        return list;
    }, [solicitudesData, seenAddresses]);

    // Conteo de direcciones nuevas
    const nuevasCount = itemsBNB.filter(item => item.isNew).length;
    const totalCount = solicitudesData.total || itemsBNB.length;
    const usuariosUnicos = useMemo(() => new Set(itemsBNB.map(i => i.usuario.toLowerCase())).size, [itemsBNB]);

    // Marcar todas como vistas
    const handleMarcarTodasVistas = () => {
        const allAddrs = itemsBNB.map(item => item.bnbAddress.toLowerCase());
        const updatedSeen = Array.from(new Set([...seenAddresses, ...allAddrs]));
        setSeenAddresses(updatedSeen);
        try {
            localStorage.setItem('seen_bnb_addresses_v1', JSON.stringify(updatedSeen));
        } catch (e) {
            console.error('Error guardando en localStorage:', e);
        }
    };

    // Copiar dirección individual al portapapeles
    const handleCopy = (address, index) => {
        navigator.clipboard.writeText(address);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    // Descargar en TXT
    const handleDownloadTxt = () => {
        const textToSave = itemsBNB.map(i => `[${i.isNew ? 'NUEVA' : 'VISTA'}] ${i.usuario} -> ${i.bnbAddress}`).join('\n');
        const blob = new Blob([textToSave], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `direcciones_bnb_${new Date().toISOString().slice(0, 10)}.txt`;
        link.click();
    };

    // Filtrar elementos según búsqueda y tab
    const filteredItems = itemsBNB.filter(item => {
        const matchesSearch = item.usuario.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.bnbAddress.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
        if (filterType === 'nuevas') return item.isNew;
        if (filterType === 'vistas') return !item.isNew;
        return true;
    });

    return (
        <Nlayout>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    px: { xs: 2, sm: 3, md: 4 },
                    pt: { xs: '80px', sm: '90px' },
                    pb: { xs: '90px', sm: '40px' }, // Espacio para el mobile menu de Nlayout
                    fontFamily: '"Outfit", sans-serif',
                    boxSizing: 'border-box'
                }}
            >
                {/* Header Superior */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 2,
                        mb: 3
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                background: 'rgba(243, 186, 47, 0.12)',
                                border: '1px solid rgba(243, 186, 47, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <FaWallet size={20} color="#f3ba2f" />
                        </Box>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: '#ffffff',
                                    fontFamily: '"Outfit", sans-serif',
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Direcciones BNB
                            </Typography>
                            {/* <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontFamily: '"Outfit", sans-serif',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Panel de gestión y consulta de billeteras vinculadas
                            </Typography> */}
                        </Box>
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={fetchSolicitudes}
                        startIcon={<FiRefreshCw className={loading ? 'spin-icon' : ''} />}
                        sx={{
                            borderColor: 'rgba(255, 255, 255, 0.12)',
                            color: '#ffffff',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontFamily: '"Outfit", sans-serif',
                            px: 2.5,
                            py: 1,
                            background: 'rgba(255, 255, 255, 0.03)',
                            '&:hover': {
                                borderColor: '#f3ba2f',
                                color: '#f3ba2f',
                                background: 'rgba(243, 186, 47, 0.08)'
                            }
                        }}
                    >
                        Actualizar
                    </Button>
                </Box>

                {/* Tarjetas Informativas de Métricas */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card
                            elevation={0}
                            sx={{
                                background: nuevasCount > 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                                border: nuevasCount > 0 ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(255, 255, 255, 0.07)',
                                borderRadius: '14px',
                                p: 2.5,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: nuevasCount > 0 ? '#10b981' : 'rgba(255, 255, 255, 0.5)',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        fontFamily: '"Outfit", sans-serif'
                                    }}
                                >
                                    Nuevas Sin Revisar
                                </Typography>
                                <Chip
                                    label={nuevasCount > 0 ? `${nuevasCount} NUEVAS` : "AL DÍA"}
                                    size="small"
                                    sx={{
                                        background: nuevasCount > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                                        color: nuevasCount > 0 ? '#34d399' : 'rgba(255, 255, 255, 0.6)',
                                        border: nuevasCount > 0 ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        fontFamily: '"Outfit", sans-serif'
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: nuevasCount > 0 ? '#10b981' : '#ffffff',
                                    fontFamily: '"Outfit", sans-serif',
                                    my: 0.5
                                }}
                            >
                                {nuevasCount}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: '"Outfit", sans-serif' }}>
                                {nuevasCount > 0 ? 'Direcciones sin verificar' : 'No hay ítems pendientes'}
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                        <Card
                            elevation={0}
                            sx={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.07)',
                                borderRadius: '14px',
                                p: 2.5,
                                height: '100%'
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontWeight: 700,
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    fontFamily: '"Outfit", sans-serif',
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Total Direcciones
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#ffffff', fontFamily: '"Outfit", sans-serif', my: 0.5 }}>
                                {totalCount}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: '"Outfit", sans-serif' }}>
                                Registros totales en la Base de datos
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                        {/* <Card
                            elevation={0}
                            sx={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.07)',
                                borderRadius: '14px',
                                p: 2.5,
                                height: '100%'
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontWeight: 700,
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    fontFamily: '"Outfit", sans-serif',
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Usuarios Únicos
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f3ba2f', fontFamily: '"Outfit", sans-serif', my: 0.5 }}>
                                {usuariosUnicos}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: '"Outfit", sans-serif' }}>
                                Cuentas registradas distintas
                            </Typography>
                        </Card> */}
                    </Grid>
                </Grid>

                {/* Barra de Filtros, Buscador y Acciones */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'rgba(20, 22, 34, 0.75)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '16px',
                        p: { xs: 2, sm: 2.5 },
                        mb: 3
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        {/* Buscador */}
                        <Grid item xs={12} md={5} style={{ width: '25%' }}>
                            <TextField
                                fullWidth
                                placeholder="Buscar por usuario o wallet BNB..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <FaSearch style={{ marginRight: 10, color: 'rgba(255, 255, 255, 0.4)', fontSize: 14 }} />,
                                    sx: {
                                        color: '#fff',
                                        fontSize: '0.9rem',
                                        fontFamily: '"Outfit", sans-serif',
                                        background: 'rgba(0, 0, 0, 0.3)',
                                        borderRadius: '10px',
                                        '& fieldset': { border: '1px solid rgba(255, 255, 255, 0.1)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                        '&.Mui-focused fieldset': { borderColor: '#f3ba2f' }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Filtros de Pestaña */}
                        {/* <Grid item xs={12} sm={7} md={4}>
                            <Box sx={{ display: 'flex', gap: 0.5, background: 'rgba(0, 0, 0, 0.3)', p: 0.5, borderRadius: '10px' }}>
                                {[
                                    { label: 'Todas', value: 'todas', count: totalCount },
                                    { label: 'Nuevas', value: 'nuevas', count: nuevasCount },
                                    { label: 'Vistas', value: 'vistas', count: totalCount - nuevasCount }
                                ].map(f => {
                                    const isActive = filterType === f.value;
                                    return (
                                        <Button
                                            key={f.value}
                                            onClick={() => setFilterType(f.value)}
                                            sx={{
                                                flex: 1,
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.8rem',
                                                fontFamily: '"Outfit", sans-serif',
                                                py: 0.8,
                                                color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                                                background: isActive ? (f.value === 'nuevas' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.1)') : 'transparent',
                                                border: isActive ? (f.value === 'nuevas' ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.15)') : '1px solid transparent',
                                                '&:hover': {
                                                    background: isActive ? (f.value === 'nuevas' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.15)') : 'rgba(255, 255, 255, 0.04)'
                                                }
                                            }}
                                        >
                                            {f.label} ({f.count})
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Grid> */}

                        {/* Botones de Acción */}
                        <Grid item xs={12} sm={5} md={3} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                            {/* <Button
                                variant="contained"
                                onClick={handleMarcarTodasVistas}
                                startIcon={<FiCheckCircle size={15} />}
                                disabled={nuevasCount === 0}
                                sx={{
                                    flex: { xs: 1, sm: 'initial' },
                                    background: 'rgba(16, 185, 129, 0.15)',
                                    color: '#34d399',
                                    border: '1px solid rgba(16, 185, 129, 0.3)',
                                    fontWeight: 600,
                                    fontFamily: '"Outfit", sans-serif',
                                    fontSize: '0.85rem',
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: 'rgba(16, 185, 129, 0.25)',
                                        borderColor: 'rgba(16, 185, 129, 0.5)',
                                        boxShadow: 'none'
                                    },
                                    '&.Mui-disabled': {
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        borderColor: 'rgba(255, 255, 255, 0.06)',
                                        color: 'rgba(255, 255, 255, 0.2)'
                                    }
                                }}
                            >
                                Marcar Vistas
                            </Button> */}

                            <Tooltip title="Descargar como TXT">
                                <IconButton
                                    onClick={handleDownloadTxt}
                                    sx={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        color: '#ffffff',
                                        borderRadius: '10px',
                                        p: '9px',
                                        '&:hover': {
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                        }
                                    }}
                                >
                                    <FaDownload size={14} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Estado de Carga o Error */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#f3ba2f' }} size={36} />
                    </Box>
                )}

                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius: '12px',
                            mb: 3,
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#fca5a5',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            fontFamily: '"Outfit", sans-serif'
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Lista de Direcciones BNB */}
                {!loading && !error && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {filteredItems.length === 0 ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 5,
                                    textAlign: 'center',
                                    background: 'rgba(20, 22, 34, 0.5)',
                                    borderRadius: '16px',
                                    border: '1px solid rgba(255, 255, 255, 0.06)'
                                }}
                            >
                                <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}>
                                    No se encontraron direcciones BNB
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)', mt: 0.5, fontFamily: '"Outfit", sans-serif' }}>
                                    {searchTerm ? 'Prueba ajustando el término de búsqueda.' : 'No hay datos cargados en esta vista.'}
                                </Typography>
                            </Paper>
                        ) : (
                            <AnimatePresence>
                                {filteredItems.map((item, index) => (
                                    <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2, delay: Math.min(index * 0.02, 0.25) }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: { xs: 1.75, sm: 2 },
                                                borderRadius: '12px',
                                                background: item.isNew
                                                    ? 'rgba(16, 185, 129, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.02)',
                                                border: item.isNew
                                                    ? '1px solid rgba(16, 185, 129, 0.3)'
                                                    : '1px solid rgba(255, 255, 255, 0.06)',
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                justifyContent: 'space-between',
                                                gap: 1.5,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: item.isNew ? 'rgba(16, 185, 129, 0.5)' : 'rgba(255, 255, 255, 0.15)',
                                                    background: item.isNew ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.04)'
                                                }
                                            }}
                                        >
                                            {/* Info Usuario */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: { sm: 200 } }}>
                                                <Box
                                                    sx={{
                                                        width: 38,
                                                        height: 38,
                                                        borderRadius: '10px',
                                                        background: item.isNew ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                                                        color: item.isNew ? '#34d399' : '#ffffff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 700,
                                                        fontSize: '0.95rem',
                                                        fontFamily: '"Outfit", sans-serif',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {item.usuario.charAt(0).toUpperCase()}
                                                </Box>

                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: '#ffffff',
                                                                fontFamily: '"Outfit", sans-serif',
                                                                fontSize: '0.95rem',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                        >
                                                            {item.usuario}
                                                        </Typography>

                                                        {item.isNew && (
                                                            <Chip
                                                                label="NUEVA"
                                                                size="small"
                                                                sx={{
                                                                    background: 'rgba(16, 185, 129, 0.2)',
                                                                    color: '#34d399',
                                                                    border: '1px solid rgba(16, 185, 129, 0.4)',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.65rem',
                                                                    height: 20,
                                                                    fontFamily: '"Outfit", sans-serif'
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {item.fecha && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'rgba(255, 255, 255, 0.4)',
                                                                display: 'block',
                                                                fontFamily: '"Outfit", sans-serif',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            {new Date(item.fecha).toLocaleString()}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Dirección BNB y Copia */}
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: 'rgba(0, 0, 0, 0.25)',
                                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                                    borderRadius: '8px',
                                                    px: 1.5,
                                                    py: 0.8,
                                                    maxWidth: '100%',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: 'Consolas, Monaco, monospace',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.8rem', sm: '0.88rem' },
                                                        color: item.isNew ? '#34d399' : '#f3ba2f',
                                                        wordBreak: 'break-all',
                                                        pr: 1
                                                    }}
                                                >
                                                    {item.bnbAddress}
                                                </Typography>

                                                <Tooltip title={copiedIndex === index ? "¡Copiado!" : "Copiar dirección"}>
                                                    <IconButton
                                                        onClick={() => handleCopy(item.bnbAddress, index)}
                                                        size="small"
                                                        sx={{
                                                            color: copiedIndex === index ? '#10b981' : 'rgba(255, 255, 255, 0.6)',
                                                            flexShrink: 0,
                                                            '&:hover': { color: '#ffffff', background: 'rgba(255, 255, 255, 0.08)' }
                                                        }}
                                                    >
                                                        {copiedIndex === index ? <FaCheck size={14} /> : <FaCopy size={14} />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Paper>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        )}
                    </Box>
                )}
            </Box>
        </Nlayout>
    );
}
